import {
  BlocksCloseEvent,
  CatButton,
  CatHeading,
  CatIconSuccess,
  CatIconWarningCircle,
  CatToast,
  CatToastGroup
} from 'components/blocks-components';
import { Conditional } from 'components/pcc-components';
import styles from '@cat-ecom/pcc-components/src/components/common/toast/style.module.scss';
import { useId } from 'react';
import { CONTACT_DEALER_LINK } from 'constants/links';
import { getSeoUrl, normalizeCommerceLink } from 'utils/links';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { BlocksCancelEvent } from '@blocks-web-components/events';
import { ToastVariant } from '@blocks-web-components/components/toast/toast.model';

interface ToastMessageProps {
  variant: ToastVariant;
  showToast: boolean;
  onToastClose: (e?: BlocksCloseEvent | BlocksCancelEvent) => void;
  toastMessage: string | React.ReactNode;
  toastContent: string | React.ReactNode;
}

const ToastMessage = ({
  variant,
  showToast,
  onToastClose,
  toastMessage,
  toastContent
}: ToastMessageProps) => {
  const internalID = useId();

  return (
    <div key={internalID}>
      <Conditional test={showToast}>
        <CatToast
          data-testid="cat-toast"
          variant={variant}
          delay={5}
          onBlToastClose={onToastClose}
          onBlToastCancel={onToastClose}
        >
          <div className={`${styles['toast-container__width']} d-flex`}>
            {variant === 'success' ? (
              <CatIconSuccess className="me-3" slot="after" color={variant} />
            ) : (
              <CatIconWarningCircle
                className="me-2"
                slot="after"
                color={variant}
              />
            )}
            <div className="d-flex flex-column">
              <CatHeading
                data-testid={`cat-heading-${variant}`}
                variant="label"
              >
                {toastMessage}
              </CatHeading>
              {toastContent}
            </div>
          </div>
        </CatToast>
      </Conditional>
    </div>
  );
};

export const DealerNotifyFailureToast = ({
  showAddEquipSuccessToast,
  showDealerNotifyErrorToast,
  setShowAddEquipSuccessToast,
  setShowDealerNotifyErrorToast,
  storeId,
  currentlyShoppingText,
  contentGuestToast
}) => {
  const t = useTranslations();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const getLink = (pathname: string) =>
    normalizeCommerceLink(getSeoUrl(params, pathname));

  const contactUsLink = getLink(`${CONTACT_DEALER_LINK}?storeId=${storeId}`);

  const onSuccessToastClose = () => {
    setShowAddEquipSuccessToast(false);
  };
  const onFailureToastClose = () => {
    setShowDealerNotifyErrorToast(false);
  };

  const contactButton = (
    <div className="mt-2">
      <CatButton size={'sm'} href={contactUsLink}>
        <span className="text-capitalize">{t('CONTACT_YOUR_DEALER')}</span>
      </CatButton>
    </div>
  );

  // show success toast for adding equipment and error toast as email failed to send
  return (
    <CatToastGroup
      data-testid="cat-toast-group"
      horizontalPosition={'center'}
      verticalPosition={'top'}
    >
      <ToastMessage
        variant={'success'}
        showToast={showAddEquipSuccessToast}
        onToastClose={onSuccessToastClose}
        toastMessage={currentlyShoppingText}
        toastContent={contentGuestToast}
      />
      <ToastMessage
        variant={'error'}
        showToast={showDealerNotifyErrorToast}
        onToastClose={onFailureToastClose}
        toastMessage={t('MEQ_ADD_NOTIFICATION8')}
        toastContent={contactButton}
      />
    </CatToastGroup>
  );
};
